import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  input,
} from '@angular/core';
import { FormControl, NgControl, Validators } from '@angular/forms';
import { IconNames } from '@app/shared/enums/icon-names';
import { InputWidth } from '@app/shared/models/input-width.model';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { NgClass, NgStyle } from '@angular/common';
import { SortByPipe } from '@app/sales/modules/mbb/pipes/sort-by.pipe';
import { getObjectPropertyFromPath } from '@app/core/utils';

export type DoubleLineText = {
  topLeftText: string;
  topRightText?: string;
  bottomLeftText?: string;
  bottomRightText?: string;
};

export type Option = {
  displayName: string | DoubleLineText;
  value: string;
};

/**
 *
 * The select is meant to be used with/without a form control that allows the user to select one option from a list of options.
 *
 * Usage example with form control:
 * @example
 * <app-ice-select ngDefaultControl formControlName="myFormControl" label="myLabel" options="myOptionsList" />
 *
 * Usage example without form control:
 * @example
 * <app-ice-select label="myLabel" options="myStringOptionsList" defaultValue="One" (optionSelected)="handleSelected" />
 *
 */
@Component({
  selector: 'app-ice-select',
  templateUrl: './ice-select.component.html',
  styleUrl: './ice-select.component.scss',
  imports: [NgClass, MatIcon, NgStyle, SortByPipe],
})
export class IceSelectComponent implements OnInit, OnDestroy {
  private controlDirective = inject(NgControl, { optional: true });
  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);

  IconNames = IconNames;

  // Label
  @Input({ required: true }) label: string;
  @Input() labelHelpText: string;
  @Input() forId = '';

  // Input
  @Input() inputWidth: InputWidth = '';
  @Input() placeholder = '';
  @Input() ngxsFormPath: string;
  @Input() control: FormControl;

  // Options
  @Input() visibleOptions: number = 5;
  _options: Option[];
  get options(): Option[] {
    return this._options ?? [];
  }
  @Input({ required: true }) set options(value: string[] | Option[]) {
    this._options = value.map(option => {
      if (typeof option === 'string') {
        return {
          displayName: option,
          value: option,
        } as Option;
      } else {
        return option;
      }
    });
  }
  @Input() defaultValue: string;

  // Icons
  @Input() icon: IconNames = IconNames.ice_arrow_down;

  // Information
  @Input() errorText: string;
  @Input() longHelpText: string;
  @Input() hideOptionalText: boolean = false;

  // Testing
  textTestId = input.required<string>();
  buttonTestId = input.required<string>();

  //Output
  @Output() optionSelected = new EventEmitter<string>();

  @ViewChild('optionsList') optionsListElement: ElementRef;

  isOptionListVisible = false;
  cancelBlur = false;

  private hasFormBeenVisited: boolean;
  private subscription: Subscription;

  @HostBinding('class') get class() {
    return this.inputWidth;
  }

  get selectedValue(): string {
    return this.control.value;
  }

  get selectedDisplayName(): string {
    const selectedOption = this.options.find(option => option.value === this.selectedValue);
    if (!selectedOption) return '';
    return this.getDoubleLineText(selectedOption)?.topLeftText ?? (selectedOption.displayName as string);
  }

  get optionListHeight(): string {
    if (!this.optionsListElement?.nativeElement.children?.length) return '100px';

    const scrollHeightList = Array.from(this.optionsListElement.nativeElement.children).map(
      (child: HTMLElement) => child.scrollHeight
    );
    const maxHeight = scrollHeightList.reduce((accumulated, current, index) => {
      if (this.visibleOptions <= index) return accumulated;
      return accumulated + current;
    }, this.visibleOptions);
    return `${maxHeight || 100}px`;
  }

  get isDisabled(): boolean {
    return this.control.disabled;
  }

  get isRequired(): boolean {
    return this.control.hasValidator(Validators.required);
  }

  get showOptionalText(): boolean {
    return !this.isRequired && !this.isDisabled && !!this.controlDirective;
  }

  get showIsRequiredError(): boolean {
    return this.control.hasError('required') && this.hasFormBeenVisited;
  }

  get isErrorState(): boolean {
    return (
      (this.control.invalid && !!this.control.value && (this.control.touched || this.hasFormBeenVisited)) ||
      this.showIsRequiredError
    );
  }

  private get selectedIndex(): number {
    return this.options.findIndex(o => o.value === this.selectedValue);
  }

  ngOnInit() {
    if (!this.control) {
      this.control = !!this.controlDirective?.control ? (this.controlDirective.control as FormControl) : new FormControl();
      this.cdr.detectChanges();
    }

    if (!!this.ngxsFormPath) {
      const state = this.store.snapshot();
      const contextForm = getObjectPropertyFromPath(state, this.ngxsFormPath);
      this.hasFormBeenVisited = contextForm?.dirty;
    }

    if (!this.selectedValue && !this.placeholder && !this.defaultValue) {
      this.control.setValue(this.options[0]?.value);
    } else if (this.defaultValue) {
      this.control.setValue(this.defaultValue);
    }

    this.subscription = this.control.valueChanges.subscribe(_ => {
      const selectedOptionElement = (Array.from(this.optionsListElement.nativeElement.children) as HTMLElement[]).find(
        child => {
          const optionInput = child.querySelector('input');
          return optionInput?.value === this.selectedValue;
        }
      );
      if (!!selectedOptionElement && this.isOptionListVisible) selectedOptionElement.scrollIntoView();
    });
  }

  toggleDropdown(): void {
    this.isOptionListVisible = !this.isOptionListVisible;
    this.control.markAsTouched();

    if (this.isOptionListVisible) {
      this.optionsListElement.nativeElement.focus();
    }
  }

  closeDropdown(): void {
    // prevent dropdown from closing before a clicked option has been selected
    if (!this.cancelBlur) {
      this.isOptionListVisible = false;
    }
  }

  handleArrowNavigation(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = this.selectedIndex + 1 >= this.options.length ? 0 : this.selectedIndex + 1;
      this.control.setValue(this.options[nextIndex]?.value);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const nextIndex = this.selectedIndex - 1 < 0 ? this.options.length - 1 : this.selectedIndex - 1;
      this.control.setValue(this.options[nextIndex]?.value);
    }
  }

  selectOption(optionValue: string): void {
    this.isOptionListVisible = false;
    this.control.markAsDirty();
    this.optionSelected.emit(optionValue);
    this.control.setValue(optionValue);
  }

  getDoubleLineText(option: Option): DoubleLineText | null {
    if (!option) return null;
    return typeof option.displayName === 'string' ? null : option.displayName;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
